/**
 * AFS Advocates — Storage Layer (Dexie / IndexedDB)
 *
 * WHY THIS EXISTS:
 * The original app used raw localStorage with giant JSON blobs.
 * That approach fails on large case histories (QuotaExceededError),
 * is slow to serialise, and is hard to query or migrate.
 *
 * This module provides a structured IndexedDB backend via Dexie.
 * - cases          — case records (no binary data)
 * - docket_entries — each docket entry as its own record
 * - deadlines      — deadline records per case
 * - evidence_meta  — evidence file metadata (no binary data)
 * - evidence_files — binary file storage (base64)
 * - blind_spots    — all blind-spot module data per case
 * - research_items — saved research per case
 * - arg_versions   — argument builder versions per case
 * - media_library  — pasted/uploaded media assets, case- or global-scoped
 *
 * MIGRATION NOTE:
 * localStorage data from the original app is migrated on first run.
 * See src/storage/migrate.ts for the migration logic.
 */

import Dexie, { type Table } from 'dexie';
import type {
  Case,
  DocketEntry,
  Deadline,
  EvidenceItem,
  ArgumentVersion,
} from '@/types';
// Phase 3A — cross-examination tree schema
// IMPORTANT: src/types/crossExam.ts must exist before this file is compiled.
import type {
  CrossExamTreeRecord,
  CrossExamSessionRecord,
} from '@/types/crossExam';
// Roadmap 1f — Contract Engine — Dexie mirror tables.
// Real field shapes live in src/contracts/types.ts (1b/1c) — same convention
// as the cross-exam import above: types stay in their domain module, this
// file only wires them into Dexie tables/indexes.
import type {
  ClientPositionProfile,
  ContractClause,
} from '@/contracts/types';
// Case Dissection — Combined Build Plan Part 2, persistence (Option B).
// Real field shapes live in src/caseDissection/types.ts — same convention
// as the contracts import directly above: types stay in their domain
// module, this file only wires them into a Dexie table/indexes.
import type { CitedCaseLibraryEntry } from '@/caseDissection/types';

// ── Supplementary record types stored in their own tables ─────────────────────

export interface EvidenceFile {
  id:   string;   // same as EvidenceItem.id
  data: string;   // base64 data URL
}

export interface BlindSpotRecord {
  id:     string;   // `${caseId}_${module}`
  caseId: string;
  module: string;
  data:   unknown;  // module-specific object — typed within each engine
}

export interface ResearchRecord {
  id:       string;
  caseId:   string;
  query:    string;
  type:     string;
  result:   string;
  note:     string;
  savedAt:  string;
}

/**
 * Argument Template — Trial Engine Consolidation, Phase 2.
 *
 * A reusable, case-agnostic argument skeleton for a given (appType,
 * jurisdiction, court_level) combination. Not scoped to a single case —
 * stored in its own global table so it can be applied across any matter.
 * Local-only (no D1 sync), same as arg_versions: these are large analytical
 * documents and the dual-write pattern isn't wired up for non-case-scoped
 * tables yet.
 */
export interface ArgumentTemplate {
  id:                  string;
  appType:             string;        // e.g. "Bail Application" — matches AppTypeConfig.label
  jurisdiction:         string;       // e.g. "Delta State" | "FCT" | "Federal"
  court_level:          string;       // e.g. "High Court" | "Magistrate"
  skeleton:             string;       // Reusable argument body (markdown) — no case-specific facts
  statutory_basis:      string;       // e.g. "s.35 CFRN, s.158 ACJL..."
  leading_authorities:  string;       // Comma-separated
  tests:                string;       // Applicable legal tests verbatim
  law_delta:            string;       // getJurisdictionDelta() output captured at creation time
  needsCaseTheory:      boolean;      // Mirrors the appType's flag in AppTypeConfig
  created_at:           string;
  updated_at:           string;
}

/**
 * Draft Buffer — Phase 7B resilient streaming.
 *
 * Every streaming call writes its partial output here as chunks arrive.
 * On successful completion the record is deleted. On connection failure
 * the partial text survives in IndexedDB so Phase 7C can resume the call
 * by prefilling an assistant-turn with the saved text and asking Claude
 * to continue from that point.
 *
 * callId    — a UUID generated by callClaude at stream start; ties the
 *             buffer record to the in-flight request.
 * engine    — queryHint / engine name for display and telemetry.
 * caseId    — the active case at the time of the call (nullable for
 *             case-agnostic calls e.g. global AICopilot).
 * partial   — the text accumulated so far.
 * startedAt — ISO timestamp; used to age-out stale records (e.g. >24 h).
 * prompt    — the user-facing prompt that initiated the call, stored so
 *             the resume flow can reconstruct the original request without
 *             the caller having to manage that state.
 */
export interface DraftBufferRecord {
  callId:    string;   // primary key — UUID
  engine:    string;
  caseId:    string | null;
  partial:   string;
  startedAt: string;   // ISO 8601
  prompt:    string;   // last user message that started this call
}

/**
 * Media Library — Phase 8A.
 *
 * A single table for pasted/uploaded media assets at two scopes:
 *   - 'case'   — caseId is set; the item belongs to one matter only
 *   - 'global' — caseId is null; a reusable template asset available
 *                across every matter (Phase 8C "Save as Template" flips
 *                an existing case-scoped item to this scope and clears
 *                caseId, rather than copying it into a second table)
 *
 * caseId is nullable specifically so global items have a clean, explicit
 * "no case" value rather than a placeholder string — Dexie indexes null
 * fine, so global items stay queryable via .where('scope').equals('global').
 *
 * data is stored inline as a base64 data URL — the same approach as
 * evidence_files — rather than split into separate meta/file tables.
 * Media-library items are expected to be opportunistically pasted/dropped
 * assets (clips, screenshots, boilerplate snippets) rather than large case
 * documents, so one row per item is enough.
 */
export interface MediaLibraryItem {
  id:        string;            // UUID
  caseId:    string | null;     // null when scope === 'global'
  scope:     'case' | 'global';
  filename:  string;
  fileType:  string;            // MIME type
  fileSize:  number;            // bytes
  data:      string;            // base64 data URL
  notes:     string;
  createdAt: string;            // ISO 8601
}

/**
 * Rebuttal Bank — Toulmin Upgrade, Phase 5A.
 *
 * Same shape as ArgumentTemplate: a reusable, case-agnostic record keyed by
 * (appType, jurisdiction), not scoped to any single case. Where a Template
 * stores a reusable argument skeleton, this stores the known defeaters for
 * that appType/jurisdiction combination — e.g. Interlocutory Injunction →
 * clean hands, undue delay, non-disclosure of material facts, adequacy of
 * damages.
 *
 * Deliberately empty of seed data at creation (Phase 5A is schema only).
 * Per the build plan, do NOT hand-seed from memory — Phase 5B seeds this
 * from what Phase 4's generateRebuttal() actually surfaces across 5–10 real
 * issues, same discipline used building argument_templates originally.
 * Phase 5C then makes rebuttal generation check this bank first and let AI
 * generation fill any case-specific gaps the bank doesn't cover.
 */
export interface RebuttalBankDefeater {
  defeater: string;   // e.g. "clean hands"
  note:     string;   // optional short gloss on when/how it applies — '' if none yet
  // Provenance — kept per-defeater (not per-record) because a real record will
  // accumulate both kinds over time as Phase 4 harvesting supplements a starter set.
  // 'seeded_unverified': entered from doctrine/hints already in the codebase, not
  //   from a real generateRebuttal() run — treat as a suggestion to check, not settled.
  // 'harvested': surfaced by generateRebuttal() on a real issue and added via Phase 5B.
  source: 'seeded_unverified' | 'harvested';
}

export interface RebuttalBankRecord {
  id:           string;                    // `${appType}::${jurisdiction}`
  appType:      string;                    // e.g. "Interlocutory Injunction" — matches AppTypeConfig.label
  jurisdiction: string;                    // e.g. "Delta State" | "FCT" | "Federal"
  defeaters:    RebuttalBankDefeater[];    // known defeaters harvested from real Phase 4 output
  created_at:   string;
  updated_at:   string;
}

export class AfsDatabase extends Dexie {
  declare cases:              Table<Case,           string>;
  declare docket_entries:     Table<DocketEntry,    string>;
  declare deadlines:          Table<Deadline,       string>;
  declare evidence_meta:      Table<EvidenceItem,   string>;
  declare evidence_files:     Table<EvidenceFile,   string>;
  declare blind_spots:        Table<BlindSpotRecord, string>;
  declare research:           Table<ResearchRecord, string>;
  declare arg_versions:       Table<ArgumentVersion & { caseId: string }, string>;
  declare argument_templates: Table<ArgumentTemplate, string>;
  declare rebuttal_bank:      Table<RebuttalBankRecord, string>;
  declare draft_buffer:       Table<DraftBufferRecord, string>;
  declare media_library:      Table<MediaLibraryItem, string>;
  // Phase 3A — cross-examination tree tables
  declare cross_exam_trees:    Table<CrossExamTreeRecord,    string>;
  declare cross_exam_sessions: Table<CrossExamSessionRecord, string>;
  // Roadmap 1f — Contract Engine — Foundation.
  // Dual-write mirrors of the D1 client_position_profile / contract_clauses
  // tables (see workers/rag-worker/src/index.ts ensureTables()). Same
  // pattern as cases/docket_entries: local table is the source of truth for
  // offline use, D1 is synced in the background via storage/helpers.ts.
  declare client_position_profile: Table<ClientPositionProfile, string>;
  declare contract_clauses:        Table<ContractClause,        string>;
  // Case Dissection — Combined Build Plan Part 2. Global, firm-wide table
  // of cited-case extractions, keyed by normalized citation. Same
  // local-only, not-scoped-to-a-case pattern as argument_templates (V3)
  // and rebuttal_bank (V7) above — see CitedCaseLibraryEntry's doc comment
  // in src/caseDissection/types.ts for why comparisons/verdicts are never
  // stored here.
  declare case_dissection_library: Table<CitedCaseLibraryEntry, string>;

  constructor() {
    super('afs_legal_os');

    this.version(1).stores({
      cases:          '&id, caseName, createdAt',
      docket_entries: '&id, caseId, dateFiled, status',
      deadlines:      '&id, caseId, date, status',
      evidence_meta:  '&id, caseId, category, timestamp',
      evidence_files: '&id',
      blind_spots:    '&id, caseId, module',
      research:       '&id, caseId, type, savedAt',
      arg_versions:   '&id, caseId, createdAt',
    });

    // V2 — adds matter_track and counsel_role indexes so the dashboard can
    // filter matters by track and role without a full table scan.
    this.version(2).stores({
      cases: '&id, caseName, createdAt, matter_track, counsel_role',
    });

    // Trial Engine Consolidation, Phase 1 — no schema/version bump needed.
    // case_theory_structured / case_theory_locked / case_theory_locked_at /
    // case_theory_score / case_theory_version / case_theory_history live as
    // plain fields on the Case record (see src/types/index.ts). They are
    // never queried via .where() so they don't need a Dexie index — Dexie
    // stores whatever shape is put() into a table regardless of the
    // declared index string. Read/write only through loadCaseTheory /
    // saveCaseTheory / lockCaseTheory / unlockCaseTheory in
    // src/storage/helpers.ts so the lock/version/history invariants hold.

    // V3 — Trial Engine Consolidation, Phase 2. New, wholly separate table
    // for reusable Argument Templates — not scoped to any case, so it has
    // its own indexes rather than piggybacking on blind_spots.
    this.version(3).stores({
      argument_templates: '&id, appType, jurisdiction, court_level, created_at',
    });

    // V4 — Phase 7B resilient streaming. draft_buffer holds in-flight
    // SSE partial output keyed by callId (UUID). Records are written on
    // every incoming chunk and deleted on clean stream completion. A record
    // that survives a page reload indicates an interrupted stream; Phase 7C
    // uses it to resume. caseId and startedAt are indexed so stale records
    // (>24 h) can be purged without a full table scan.
    this.version(4).stores({
      draft_buffer: '&callId, caseId, startedAt',
    });

    // V5 — Phase 8A. media_library holds pasted/uploaded assets at two
    // scopes: case-scoped (caseId set, scope:'case') and global/template
    // scoped (caseId null, scope:'global'). caseId and scope are both
    // indexed so a later query (Phase 8E) can fetch case-scoped + global
    // items in two cheap lookups and merge them client-side, rather than
    // scanning the whole table.
    this.version(5).stores({
      media_library: '&id, caseId, scope, createdAt',
    });

    // V6 — Phase 3A. Cross-examination offline tree system.
    //
    // cross_exam_trees:
    //   Primary key is a composite string `${caseId}::${witnessId}::${topicId}`.
    //   Indexed by caseId + witnessId so the Phase 3B topic-selection UI and
    //   Phase 4 session walker can load all trees for a witness in one query.
    //   trialReady is indexed so a pre-flight check can confirm all selected
    //   trees are approved without iterating every node.
    //
    // cross_exam_sessions:
    //   Primary key is a UUID. Indexed by caseId + witnessId so the Phase 4
    //   session resume logic can find the most-recent open session efficiently.
    //   endedAt is indexed (nullable) so Phase 5A can query sessions that are
    //   closed but not yet fed to Contradiction Mapper.
    this.version(6).stores({
      cross_exam_trees:    '&id, caseId, witnessId, [caseId+witnessId], trialReady, generatedAt',
      cross_exam_sessions: '&id, caseId, witnessId, [caseId+witnessId], startedAt, endedAt',
    });

    // V7 — Toulmin Upgrade, Phase 5A. rebuttal_bank holds known defeaters per
    // (appType, jurisdiction), same shape/indexing pattern as argument_templates
    // (V3 above). Deliberately created empty — see RebuttalBankRecord doc comment.
    this.version(7).stores({
      rebuttal_bank: '&id, appType, jurisdiction',
    });

    // V8 — Roadmap 1f. Contract Engine Dexie mirrors.
    //
    // client_position_profile: one row per contract (in practice), but
    // primary-keyed by its own `id` rather than `contract_id` directly —
    // matches the ClientPositionProfile shape (1b), which already carries
    // both `id` and `contract_id`. Indexed by contract_id so
    // loadClientPositionProfile() can look it up without a full scan.
    //
    // contract_clauses: many rows per contract. Indexed by contract_id
    // (primary lookup), jurisdiction (mirrors the D1 column — 2d's library
    // filter reads this directly, same reasoning as the D1 schema comment),
    // and round_number (Negotiate Mode, 4a, needs "clauses at round N").
    this.version(8).stores({
      client_position_profile: '&id, contract_id, updated_at',
      contract_clauses:        '&id, contract_id, jurisdiction, round_number, updated_at',
    });

    // V9 — Case Dissection, Combined Build Plan Part 2 (persistence,
    // Option B). case_dissection_library holds extractions only —
    // material_facts + ratio per citation, reused across every matter
    // that cites the same case. Primary-keyed by the normalized citation
    // string. updated_at is indexed for a future "recently dissected"
    // view; last_used_at is indexed so stale/never-reused entries could
    // be surfaced or pruned later without a full table scan.
    this.version(9).stores({
      case_dissection_library: '&id, case_name, updated_at, last_used_at',
    });
  }
}

/** Singleton database instance — import this everywhere */
export const db = new AfsDatabase();
